import { useNavigate } from "react-router-dom";
import { useFavorites } from "../contexts/FavoritesContext";
import { useSportsData } from "../contexts/SportsDataContext";
import { Star, Television, Clock, Trophy, FlagCheckered, Lightning } from "@phosphor-icons/react";

export default function GameCard({ game }) {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { changedScores } = useSportsData();

  const isLive = game.statusState === "in";
  const isFinal = game.statusState === "post";
  const isPre = game.statusState === "pre";
  const isIndividual = game.isIndividual;
  const isMMA = game.isMMA;
  const hasScoreChanged = !!changedScores[game.id];

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };

  return (
    <div
      className={`bg-[#141414] border border-white/10 rounded-lg overflow-hidden game-card-hover cursor-pointer relative group ${hasScoreChanged ? "score-flash" : ""}`}
      data-testid={`game-card-${game.id}`}
      onClick={() => navigate(`/game/${game.league}/${game.id}`)}
      style={game.homeTeam?.color ? { borderTopColor: `#${game.homeTeam.color}30`, borderTopWidth: "2px" } : {}}
    >
      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/5">
        <div className="flex items-center gap-2">
          {isLive && (
            <span className="inline-flex items-center gap-1 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 px-2 py-0.5 rounded text-[10px] tracking-widest uppercase font-bold" data-testid={`live-badge-${game.id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-live-pulse" />
              LIVE
            </span>
          )}
          {isFinal && (
            <span className="text-[10px] tracking-widest uppercase font-bold text-white/40">FINAL</span>
          )}
          {isPre && (
            <span className="text-[10px] tracking-widest uppercase text-white/40 flex items-center gap-1">
              <Clock size={10} /> {formatDate(game.date)}
            </span>
          )}
          <span className="text-[10px] tracking-wider uppercase text-[#007AFF] font-semibold">{game.leagueName}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); toggleFavorite(game.id); }}
          className="p-1 rounded hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
          data-testid={`favorite-btn-${game.id}`}
        >
          <Star size={14} weight={isFavorite(game.id) ? "fill" : "regular"} className={isFavorite(game.id) ? "text-[#007AFF]" : "text-white/30"} />
        </button>
      </div>

      {isMMA ? (
        /* UFC Fight Card */
        <div className="px-3 py-2.5">
          <div className="flex items-center gap-2 mb-2">
            <Lightning size={16} weight="fill" className="text-[#FF3B30]" />
            <span className="text-sm font-semibold text-white truncate">{game.name}</span>
          </div>
          {(game.fights || []).slice(-3).reverse().map((fight, idx) => (
            <div key={idx} className={`flex items-center gap-2 py-1 ${idx > 0 ? "border-t border-white/5" : ""}`}>
              <div className="flex-1 flex items-center gap-1.5 min-w-0">
                {fight.fighter1Flag && <img src={fight.fighter1Flag} alt="" className="w-4 h-4 rounded-full" />}
                <span className={`text-xs truncate ${fight.fighter1Winner ? "text-[#34C759] font-bold" : "text-white/60"}`}>
                  {fight.fighter1Short || fight.fighter1}
                </span>
              </div>
              <span className="text-[10px] text-white/20 px-1">vs</span>
              <div className="flex-1 flex items-center gap-1.5 justify-end min-w-0">
                <span className={`text-xs truncate ${fight.fighter2Winner ? "text-[#34C759] font-bold" : "text-white/60"}`}>
                  {fight.fighter2Short || fight.fighter2}
                </span>
                {fight.fighter2Flag && <img src={fight.fighter2Flag} alt="" className="w-4 h-4 rounded-full" />}
              </div>
            </div>
          ))}
          {(game.fights || []).length > 3 && (
            <p className="text-[10px] text-white/30 text-center mt-1">+{game.fights.length - 3} more fights</p>
          )}
        </div>
      ) : isIndividual ? (
        /* Individual sport (Golf / NASCAR) */
        <div className="px-3 py-2.5">
          <div className="flex items-center gap-2 mb-2">
            {game.league === "nascar" ? <FlagCheckered size={16} className="text-white/50" /> : <Trophy size={16} className="text-[#34C759]" />}
            <span className="text-sm font-semibold text-white truncate">{game.name}</span>
          </div>
          {/* Winner highlight for finished races */}
          {isFinal && game.awayTeam?.winner && (
            <div className="flex items-center gap-2 mb-1.5 bg-[#34C759]/10 border border-[#34C759]/20 rounded px-2 py-1.5">
              {game.awayTeam.logo && <img src={game.awayTeam.logo} alt="" className="w-5 h-5 object-contain rounded-full" />}
              <Trophy size={14} weight="fill" className="text-[#34C759]" />
              <span className="text-xs font-bold text-[#34C759]">WINNER</span>
              <span className="flex-1 text-xs font-semibold text-white truncate">{game.awayTeam.displayName || game.awayTeam.name}</span>
            </div>
          )}
          {/* Top finishers for completed, or leaders for live */}
          {(!isFinal || !game.awayTeam?.winner) && game.awayTeam?.name && game.awayTeam.name !== "TBD" && game.awayTeam.name !== "Leader" && (
            <div className="flex items-center gap-2 text-white/70">
              {game.awayTeam.logo && <img src={game.awayTeam.logo} alt="" className="w-5 h-5 object-contain rounded-full" />}
              <span className="text-[10px] text-white/40 w-5 text-center">1st</span>
              <span className="flex-1 text-xs font-medium truncate">{game.awayTeam.displayName || game.awayTeam.name}</span>
            </div>
          )}
          {game.homeTeam?.name && game.homeTeam.name !== "TBD" && game.homeTeam.name !== "2nd" && (
            <div className="flex items-center gap-2 text-white/50 mt-1">
              {game.homeTeam.logo && <img src={game.homeTeam.logo} alt="" className="w-5 h-5 object-contain rounded-full" />}
              <span className="text-[10px] text-white/30 w-5 text-center">2nd</span>
              <span className="flex-1 text-xs font-medium truncate">{game.homeTeam.displayName || game.homeTeam.name}</span>
            </div>
          )}
          {/* Show top 5 if available */}
          {isFinal && game.top5?.length > 2 && (
            <div className="mt-1.5 pt-1.5 border-t border-white/5 space-y-0.5">
              {game.top5.slice(2, 5).map((d) => (
                <div key={d.position} className="flex items-center gap-2 text-white/30">
                  {d.flag && <img src={d.flag} alt="" className="w-4 h-4 object-contain rounded-full" />}
                  <span className="text-[10px] w-5 text-center">{d.position}rd</span>
                  <span className="flex-1 text-[10px] truncate">{d.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Standard team sport */
        <div className="px-3 py-2 space-y-1.5">
          <div className={`flex items-center gap-2 ${game.awayTeam.winner ? "text-white" : "text-white/60"}`}>
            {game.awayTeam.logo ? (
              <img src={game.awayTeam.logo} alt="" className="team-logo-img" loading="lazy" />
            ) : (
              <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center text-[10px] font-bold">{game.awayTeam.abbreviation?.slice(0,2)}</div>
            )}
            <span className="flex-1 text-sm font-semibold truncate">{game.awayTeam.abbreviation || game.awayTeam.name}</span>
            {game.awayTeam.records?.[0] && <span className="text-[10px] text-white/30 mr-1">{game.awayTeam.records[0]}</span>}
            <span className={`score-font text-lg ${game.awayTeam.winner ? "text-white" : ""}`}>{(!isPre) ? game.awayTeam.score : ""}</span>
          </div>
          <div className={`flex items-center gap-2 ${game.homeTeam.winner ? "text-white" : "text-white/60"}`}>
            {game.homeTeam.logo ? (
              <img src={game.homeTeam.logo} alt="" className="team-logo-img" loading="lazy" />
            ) : (
              <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center text-[10px] font-bold">{game.homeTeam.abbreviation?.slice(0,2)}</div>
            )}
            <span className="flex-1 text-sm font-semibold truncate">{game.homeTeam.abbreviation || game.homeTeam.name}</span>
            {game.homeTeam.records?.[0] && <span className="text-[10px] text-white/30 mr-1">{game.homeTeam.records[0]}</span>}
            <span className={`score-font text-lg ${game.homeTeam.winner ? "text-white" : ""}`}>{(!isPre) ? game.homeTeam.score : ""}</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-3 py-1.5 border-t border-white/5 flex items-center justify-between">
        <span className="text-[10px] text-white/30 truncate">{game.statusDetail}</span>
        {game.broadcast && (
          <span className="text-[10px] text-white/30 flex items-center gap-1">
            <Television size={10} /> {game.broadcast}
          </span>
        )}
      </div>
    </div>
  );
}
